/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Recipient,
  RecipientDataWithPaginated,
} from "../interfaces/reducersInterfaces";
import axios from "axios";
import { MessagesDataWithPagination } from "../../Interfaces/messagesInterface";
import { Label } from "./authSlice";
import { playNotificationSound } from "../../Utils/notificationSound";
import icon192 from "../../assets/192.png";
import icon32 from "../../assets/32.png";

import { addEsChatToConfig, handleAxiosError } from "../../Utils/apiUtils";
import { handleMediaSend } from "./mediaMessageSlice";

/** Leading/trailing whitespace on outgoing text and media captions */
function trimOutgoingCopy(s: string | undefined | null): string {
  if (s == null || typeof s !== "string") return "";
  return s.trim();
}

function normalizeGlobalLabels(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((label): label is string => typeof label === "string");
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    try {
      return normalizeGlobalLabels(JSON.parse(trimmedValue));
    } catch {
      return [trimmedValue];
    }
  }

  return [];
}

// Get the initial notification state from localStorage
const getBrowserNotificationState = (): boolean => {
  try {
    const savedState = localStorage.getItem("browserNotificationEnabled");
    // Only return true if both the value is 'true' AND permission was previously granted
    return savedState === "true" && Notification.permission === "granted";
      } catch (error) {
    return false;
  }
};

interface Bot {
  id: number;
  created_at: number;
  user_id: string;
  bot_name: string;
  mission: string;
  system_prompt: string;
  knowledge_base: string;
  tone: string;
}

interface MessengerState {
  recipients: RecipientDataWithPaginated;
  messages: MessagesDataWithPagination;
  currentRecipient: Recipient | null;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: string | any;
  recipientsNextPage: number | null;
  searchKeyword?: string;
  selectedLabels: string[];
  system_user: string;
  isBrowserNotificationOn: boolean;
  bots: Bot[];
  botsLoading: boolean;
  botsError?: string | any;
  scheduledMessages: any[];
  scheduledMessagesLoading: boolean;
  scheduledMessagesError?: string | any;
  legacyScheduledMessages: any[];
  legacyScheduledMessagesLoading: boolean;
  legacyScheduledMessagesError?: string | any;
}

const initialState: MessengerState = {
  recipients: {} as RecipientDataWithPaginated,
  messages: {} as MessagesDataWithPagination,
  currentRecipient: {} as Recipient,
  recipientsNextPage: 1,
  searchKeyword: "",
  selectedLabels: [] as string[],
  system_user: "",
  isBrowserNotificationOn: getBrowserNotificationState(),
  bots: [],
  botsLoading: false,
  scheduledMessages: [],
  scheduledMessagesLoading: false,
  legacyScheduledMessages: [],
  legacyScheduledMessagesLoading: false,
};

//Get recipients
export const getRecipients = createAsyncThunk(
  "messenger/getRecipients",
  async (
    {
      page,
      searchKeyword,
      selectedLabels,
      status,
    }: { 
      page: number; 
      searchKeyword?: string; 
      selectedLabels?: string[];
      status?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const requestParams = {
        page_number: page - 1, // API uses 0-based page: 0 for first, then 1, 2, 3... on scroll
        per_page: 20,
        search: searchKeyword || "",
        labels: Array.isArray(selectedLabels) ? selectedLabels : [],
        status: status || ""
      };

      const paramsSerializer = (params: any) => {
        const parts: string[] = [];
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            // Pass as single array: labels[]=A&labels[]=B (all labels in one array)
            if (value.length === 0) {
              parts.push(`${encodeURIComponent(key)}=[]`);
            } else {
              value.forEach((item) => {
                parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`);
              });
            }
          } else if (value !== null && value !== undefined && value !== '') {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
          } else if (value === '') {
            // Include empty strings
            parts.push(`${encodeURIComponent(key)}=`);
          }
        });
        return parts.join('&');
      };

      const queryString = paramsSerializer(requestParams);
      const baseUrl = "https://api.insightssystem.com/api:bVXsw_FD/web/recipient";
      // eslint-disable-next-line no-console -- dev: inspect recipient list request URL
      console.log("getRecipients URL:", `${baseUrl}?${queryString}`);

      const config = addEsChatToConfig({
        params: requestParams,
        paramsSerializer,
      });

      const response = await axios.get(baseUrl, config);

      // Transform the response to match the expected format
      const transformedItems = response.data.map((item: any) => {
        // Determine the last message content
        const lastMessageContent = item.system_user || item.recipient || "";
        
        // Handle global_label defensively so malformed API values do not crash the UI.
        const globalLabel = normalizeGlobalLabels(item.global_label);
        
        return {
          recipient: {
            id: item.id,
            created_at: item.created_at,
            user_id: item.user_id,
            name: item.name,
            phone_number: item.phone_number,
            country: item.country,
            global_label: globalLabel,
            note: item.note,
            is_ai_assistant: item.is_ai_assistant,
            phone_number_string: item.phone_number_string,
            expiration_timestamp: item.expiration_timestamp,
            last_message_time: item.last_message_time,
            recipient_last_message_time: item.recipient_last_message_time,
            note_auto: item.note_auto,
            ai_bot_id: item.ai_bot_id || null
          },
          conversation: {
            message_id: item.message_id || "",
            last_message_id: item.last_message_id || "",
            system_user: item.system_user || "",
            recipient: item.recipient || "",
            read_time: item.read_time || null,
            status: item.status || "read",
            created_at: item.last_message_time || null,
            phone_number: item.phone_number || "",
            user_id: item.user_id || "",
            text: lastMessageContent || ""
          }
        };
      });

      // Stop scrolling when array is empty or less than full page (no more pages)
      const count = transformedItems.length;
      const fullPageSize = 20;
      const hasMore = count > 0 && count === fullPageSize;

      return {
        items: transformedItems,
        itemsReceived: transformedItems.length,
        curPage: page,
        nextPage: hasMore ? page + 1 : null,
        prevPage: page > 1 ? page - 1 : null,
        offset: (page - 1) * 20,
        perPage: 20,
        itemsTotal: transformedItems.length, // We don't have total count from API, using current items length
        pageTotal: Math.ceil(transformedItems.length / 20)
      };
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      } else {
        return rejectWithValue("An unexpected error occurred");
      }
    }
  }
);

//Get bots
export const getBots = createAsyncThunk(
  "messenger/getBots",
  async (_, { rejectWithValue }) => {
    try {
      const config = addEsChatToConfig();
      
      const response = await axios.get(
        "https://api.insightssystem.com/api:Sc_sezER/ai_config",
        config
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Failed to fetch bots");
      } else {
        return rejectWithValue("An unexpected error occurred");
      }
    }
  }
);

export const getConversation = createAsyncThunk(
  "messenger/getConversation",
  async (
    {
      perPage,
      pageNumber,
      offset,
      phoneNumber,
    }: {
      perPage: number;
      pageNumber: number;
      offset: number;
      phoneNumber: number;
    },
    { rejectWithValue }
  ) => {
    const per_page = perPage;
    const page_number = pageNumber;
    const offsetNumber = offset || 0;
    const phone_number = phoneNumber;

    try {
      const config = addEsChatToConfig();
      
      const response = await axios.get(
        `https://api.insightssystem.com/api:bVXsw_FD/web/conversations?phone_number=${phone_number}&page_number=${page_number}&per_page=${per_page}&offset=${offsetNumber}`,
        config
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// Get scheduled messages for current recipient
// NOTE: Replace endpoint/query params as needed when API is ready.
export const getScheduledMessages = createAsyncThunk(
  "messenger/getScheduledMessages",
  async (
    {
      phoneNumber,
    }: {
      phoneNumber: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      const response = await axios.get(
        `https://api.insightssystem.com/api:bVXsw_FD/web/scheduled_messages?phone_number=${phoneNumber}`,
        config
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Failed to fetch scheduled messages");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const deleteScheduledMessage = createAsyncThunk(
  "messenger/deleteScheduledMessage",
  async (
    {
      id,
    }: {
      id: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      await axios.delete(
        `https://api.insightssystem.com/api:bVXsw_FD/web/scheduled_messages?id=${id}`,
        config
      );
      return { id };
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Failed to delete scheduled message");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const getLegacyScheduledMessages = createAsyncThunk(
  "messenger/getLegacyScheduledMessages",
  async (
    {
      phoneNumber,
    }: {
      phoneNumber: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      const response = await axios.get(
        `https://api.insightssystem.com/api:bVXsw_FD/web/scheduled_messages_legacy?phone_number=${phoneNumber}`,
        config
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Failed to fetch legacy scheduled messages");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const deleteLegacyScheduledMessage = createAsyncThunk(
  "messenger/deleteLegacyScheduledMessage",
  async (
    { id }: { id: number },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      await axios.delete(
        `https://api.insightssystem.com/api:bVXsw_FD/web/scheduled_messages_legacy?id=${id}`,
        config
      );
      return { id };
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Failed to delete legacy scheduled message");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const updateRecipient = createAsyncThunk(
  "messenger/updateRecipient",
  async (
    recipientData: {
      id: number;
      name?: string;
      note?: string;
      country?: string;
      created_at?: number;
      manual_label?: string[];
      global_label?: string[];
      phone_number?: number;
      is_ai_assistant?: boolean;
      phone_number_string?: string;
      ai_bot_id?: number | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      
      const response = await axios.patch(
        `https://api.insightssystem.com/api:bVXsw_FD/web/recipient/${recipientData.id}`,
        recipientData,
        config
      );
      return response.data;
    } catch (error) {
      handleAxiosError(error);
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

//Update message recipient status
export const updateMessageStatus = createAsyncThunk(
  "messenger/updateRecipientMessageStatus",
  async (
    {
      phone_number,
      status,
    }: {
      phone_number?: number | string;
      status: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const config = addEsChatToConfig();
      
      const response = await axios.patch(
        "https://api.insightssystem.com/api:bVXsw_FD/web/conversations",
        {
          phone_number,
          status,
        },
        config
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

interface SendTextMessagePayload {
  text: string;
  currentRecipient: Recipient;
  is_schedule?: boolean;
  schedule_type?: "relative" | "specific";
  unit_of_time_name?: string;
  unit_of_time_value?: string;
  schedule_datetime_date?: string;
  time_zone?: string;
  condition_one?: string | null;
  condition_two?: {
    c?: string;
    v?: string[];
  };
  /** Current user id (auth); used for optimistic outgoing bubble */
  senderUserId?: string | null;
  /** From /auth/me `name`; shown next to time on optimistic bubble */
  senderDisplayName?: string | null;
}

export const sendTextMessage = createAsyncThunk(
  "messenger/sendTextMessage",
  async (payload: SendTextMessagePayload, { rejectWithValue }) => {
    

    if (!payload.currentRecipient) {
      return rejectWithValue("Recipient is required");
    }

    const text = trimOutgoingCopy(payload.text);
    if (!text) {
      return rejectWithValue("Message is empty");
    }

    try {


      const requestPayload = {
        text,
        phone_number: payload.currentRecipient.phone_number,
        is_schedule: payload.is_schedule ?? false,
        ...(payload.is_schedule
          ? {
              schedule_type: payload.schedule_type,
              ...(payload.schedule_type === "relative"
                ? {
                    unit_of_time_name: payload.unit_of_time_name,
                    unit_of_time_value: payload.unit_of_time_value,
                  }
                : {
                    schedule_datetime_date: payload.schedule_datetime_date,
                    time_zone: payload.time_zone,
                  }),
              condition_one: payload.condition_one ?? null,
              condition_two: payload.condition_two ?? {},
            }
          : {}),
      };

      const config = addEsChatToConfig();
      
      const response = await axios.post(
        "https://api.insightssystem.com/api:bVXsw_FD/web/send/text",
        requestPayload,
        config
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Failed to send message"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

/**
 * Socket message filtering:
 * - IF role === "Team Member" AND userLabels.length > 0: add only if message labels intersect userLabels.
 * - ELSE: add to UI (no filtering for Super Admin, Admin, Team Member (All Labels)).
 */
function shouldShowMessageByRole(
  role: string | null | undefined,
  userLabels: string[],
  messageLabels: unknown
): boolean {
  if (role !== "Team Member") return true;
  if (!userLabels || userLabels.length === 0) return true;
  const msgLabels = normalizeGlobalLabels(messageLabels);
  return msgLabels.some((l) => userLabels.includes(l));
}

/** Match optimistic bubble to incoming socket message (text or media/voice). */
function findOptimisticMergeIndex(
  items: any[],
  newMessage: any
): number {
  return items.findIndex((msg) => {
    if (typeof msg.message_id !== "string" || !msg.message_id.startsWith("optimistic-")) {
      return false;
    }
    if (msg.user_id !== newMessage.user_id) return false;

    const optType = msg.system_user_payload?.type;
    const newType = newMessage.system_user_payload?.type;
    const optMedia = msg.media_url;
    const newMedia = newMessage.media_url;

    if (msg.system_user !== newMessage.system_user) return false;

    if (optType === "text" && !optMedia) {
      return !newMedia;
    }

    if (optMedia && optType && optType !== "text") {
      if (newType && newType !== optType) return false;
      if (optMedia && newMedia && String(optMedia) === String(newMedia)) {
        return true;
      }
      const firstIdx = items.findIndex((m) => {
        if (typeof m.message_id !== "string" || !m.message_id.startsWith("optimistic-")) {
          return false;
        }
        if (m.user_id !== newMessage.user_id) return false;
        if (m.system_user !== newMessage.system_user) return false;
        if (!m.media_url || !m.system_user_payload?.type) return false;
        return m.system_user_payload?.type === optType;
      });
      return firstIdx !== -1 && items.indexOf(msg) === firstIdx;
    }

    return false;
  });
}

function buildOptimisticMediaMessageFields(
  mediaType: string,
  media_link: string,
  caption: string
): {
  media_url: string;
  system_user: string;
  system_user_payload: any;
} {
  const t =
    mediaType === "image" || mediaType === "video" || mediaType === "audio"
      ? mediaType
      : "document";
  const base: { media_url: string; system_user: string; system_user_payload: any } = {
    media_url: media_link,
    system_user: caption,
    system_user_payload: { type: t },
  };
  if (t === "image") {
    base.system_user_payload = { type: "image", image: { link: media_link } };
  } else if (t === "video") {
    base.system_user_payload = { type: "video", video: { link: media_link } };
  } else if (t === "audio") {
    base.system_user_payload = { type: "audio", audio: { link: media_link } };
  } else {
    base.system_user_payload = {
      type: "document",
      document: { link: media_link, mime_type: "application/octet-stream" },
    };
  }
  return base;
}

const messengerSlice = createSlice({
  name: "messenger",
  initialState,
  reducers: {
    newRecipientWithMessage(state, action) {
      const { newRecipient } = action.payload;
      const system_user_id = action.payload.system_user_id || state.system_user;
      const role = action.payload.role ?? null;
      const userLabels = action.payload.userLabels ?? [];

      if (system_user_id !== newRecipient.recipient.user_id) return;

      const messageLabels =
        (newRecipient.conversation as { labels?: unknown })?.labels ??
        newRecipient.recipient.global_label ??
        [];
      if (!shouldShowMessageByRole(role, userLabels, messageLabels)) return;

      const existingRecipient = state.recipients.items.find(
        (item) =>
          item.recipient.phone_number === newRecipient.recipient.phone_number
      );
      if (!existingRecipient) {
        state.recipients.items.push(newRecipient);
      } else {
        // Same event used when label is added to existing thread: update recipient (e.g. global_label) and conversation
        existingRecipient.recipient = { ...existingRecipient.recipient, ...newRecipient.recipient };
        if (newRecipient.conversation) {
          existingRecipient.conversation = { ...existingRecipient.conversation, ...newRecipient.conversation };
        }
      }
    },
    newMessageByWebhook(state, action) {
      state.loading = true;
      const newMessage = action.payload.message;
      const currentRecipient = action.payload.currentRecipient;
      const labels = action.payload.currentLabels;
      const system_user_id = action.payload.system_user_id;
      const role = action.payload.role ?? null;
      const userLabels = action.payload.userLabels ?? [];

      if (newMessage.user_id !== system_user_id) {
        state.loading = false;
        return;
      }

      // Labels: payload.recipient (when same structure as newRecipientWithMessage), or message.labels / recipient_info_of_user / message.recipient
      const payloadRecipient = action.payload.recipient as { global_label?: unknown } | undefined;
      const msg = newMessage as { labels?: unknown; recipient_info_of_user?: { global_label?: unknown }; recipient?: { global_label?: unknown } };
      const messageLabels =
        payloadRecipient?.global_label ??
        msg.labels ??
        msg.recipient_info_of_user?.global_label ??
        msg.recipient?.global_label;

      if (!shouldShowMessageByRole(role, userLabels, messageLabels)) {
        state.loading = false;
        return;
      }
      


      //notification
      if (
        newMessage.user_id === system_user_id &&
        state.isBrowserNotificationOn &&
        newMessage.recipient &&
        newMessage.phone_number &&
        Notification.permission === "granted"
      ) {
        try {
          new Notification(`New message from +${newMessage.phone_number}`, {
            body: newMessage.recipient,
            tag: `message-${newMessage.phone_number}`,
            requireInteraction: true,
            icon: icon192,
            badge: icon32,
            silent: false,
          });

          playNotificationSound();
        } catch (error) {
          // Error showing notification
        }
      }


      
      if (
        newMessage.user_id === system_user_id &&
        newMessage.phone_number === currentRecipient?.phone_number
      ) {
        const existingMessageIndex = state.messages.items.findIndex(
          (msg) => msg.message_id === newMessage.message_id
        );

        if (existingMessageIndex !== -1) {
          // Update existing message
          state.messages.items[existingMessageIndex] = {
            ...state.messages.items[existingMessageIndex],
            ...newMessage,
          };
        } else {
          // Add new message if it matches the current recipient
          if (newMessage.phone_number === currentRecipient?.phone_number) {
            // Initialize messages.items array if it doesn't exist
            if (!state.messages.items) {
              state.messages.items = [];
            }
            const optimisticIndex = findOptimisticMergeIndex(
              state.messages.items,
              newMessage
            );
            if (optimisticIndex !== -1) {
              const prev = state.messages.items[optimisticIndex];
              state.messages.items[optimisticIndex] = {
                ...prev,
                ...newMessage,
                send_by: newMessage.send_by ?? prev.send_by,
              };
            } else {
              state.messages.items.push(newMessage);
            }
          }
        }
      }

      // First, create a function to handle label matching to avoid code duplication
      const getUpdatedLabels = (
        existingLabels: unknown,
        messageText: string,
        labels: Label[]
      ) => {
        const updatedLabels = normalizeGlobalLabels(existingLabels);

        labels.forEach((label: Label) => {
          if (
            label.keywords?.length > 0 &&
            !updatedLabels.includes(label.label)
          ) {
            const hasMatchingKeyword = label.keywords.some(
              (keyword: string) => {
                const regex = new RegExp(`\\b${keyword.toLowerCase()}\\b`);
                return regex.test(messageText.toLowerCase());
              }
            );

            if (hasMatchingKeyword) {
              updatedLabels.push(label.label);
            }
          }
        });

        return updatedLabels;
      };

      // Function to merge global labels
      const mergeGlobalLabels = (existingLabels: unknown = [], newGlobalLabels: unknown = []) => {
        const uniqueLabels = new Set([
          ...normalizeGlobalLabels(existingLabels),
          ...normalizeGlobalLabels(newGlobalLabels),
        ]);
        return Array.from(uniqueLabels);
      };

      // update both currentRecipient and recipients list
      if (
        state.currentRecipient?.user_id === newMessage.user_id &&
        state.currentRecipient?.phone_number === newMessage.phone_number
      ) {
        let updatedCurrentLabels = normalizeGlobalLabels(state.currentRecipient?.global_label);

        // Update labels based on message text if recipient text exists
        if (newMessage.recipient) {
          updatedCurrentLabels = getUpdatedLabels(
            updatedCurrentLabels,
            newMessage.recipient,
            labels
          );
        }

        // Merge with global_label from webhook if it exists
        if (newMessage.recipient_info_of_user?.global_label) {
          updatedCurrentLabels = mergeGlobalLabels(
            updatedCurrentLabels,
            newMessage.recipient_info_of_user.global_label
          );
        }

        // Update currentRecipient with new labels and note_auto
        state.currentRecipient = {
          ...(state.currentRecipient as Recipient),
          last_message_time: newMessage.created_at,
          global_label: updatedCurrentLabels,
          // Only update recipient_last_message_time if it's not a template message conversation
          ...(newMessage.recipient && !state.currentRecipient?._isTemplateMessage && {
            recipient_last_message_time: newMessage.created_at
          }),
          ...(newMessage.recipient_info_of_user?.note_auto !== undefined && {
            note_auto: newMessage.recipient_info_of_user.note_auto
          })
        };
      }

      let recipientWasUpdated = false;
      state.recipients.items = state.recipients.items.map((item) => {
        if (
          item.recipient.phone_number === newMessage.phone_number &&
          newMessage.user_id === system_user_id
        ) {
          recipientWasUpdated = true;
          const isDifferentMessage =
            item.conversation?.message_id !== newMessage.message_id;

          let updatedGlobalLabels = normalizeGlobalLabels(item.recipient.global_label);

          // Update labels based on message text if it's a different message
          if (isDifferentMessage && newMessage.recipient) {
            updatedGlobalLabels = getUpdatedLabels(
              updatedGlobalLabels,
              newMessage.recipient,
              labels
            );
          }

          // Merge with global_label from webhook if it exists
          if (newMessage.recipient_info_of_user?.global_label) {
            updatedGlobalLabels = mergeGlobalLabels(
              updatedGlobalLabels,
              newMessage.recipient_info_of_user.global_label
            );
          }

          let noteAuto = item.recipient.note_auto;
          if (newMessage.recipient_info_of_user?.note_auto !== undefined) {
            noteAuto = newMessage.recipient_info_of_user.note_auto;
          }

          return {
            ...item,
            conversation: isDifferentMessage
              ? newMessage
              : { ...item.conversation, ...newMessage },
            recipient: {
              ...item.recipient,
              last_message_time:
                newMessage.created_at ?? item.recipient.last_message_time,
              // Only update recipient_last_message_time if it's not a template message conversation
              recipient_last_message_time: newMessage.recipient && !item.recipient._isTemplateMessage
                ? newMessage.created_at
                : item.recipient.recipient_last_message_time,
              global_label: updatedGlobalLabels,
              note_auto: noteAuto
            },
          };
        }
        return item;
      });

      const recipientInfo = newMessage.recipient_info_of_user;
      const canAddRecipientFromConversation =
        recipientInfo &&
        newMessage.message_id &&
        recipientInfo.user_id === system_user_id &&
        String(recipientInfo.phone_number) === String(newMessage.phone_number);

      if (!recipientWasUpdated && canAddRecipientFromConversation) {
        state.recipients.items.unshift({
          recipient: {
            ...recipientInfo,
            last_message_time:
              newMessage.created_at ??
              recipientInfo.last_message_time,
          },
          conversation: newMessage,
        });
      }
    },
    setCurrentRecipient: (state, action) => {
      state.currentRecipient = action.payload;
    },
    setSearchKeyword: (state, action) => {
      state.searchKeyword = action.payload;
      state.recipientsNextPage = null;
    },
    resetRecipients: (state) => {
      state.recipients = { ...state.recipients, items: [] }; // 🔹 Clear previous data
      state.recipientsNextPage = 1;
      state.searchKeyword = "";
    },
    addSelectedLabel: (state, action) => {
      if (!state.selectedLabels.includes(action.payload)) {
        state.selectedLabels.push(action.payload);
      }
    },
    removeSelectedLabel: (state, action) => {
      state.selectedLabels = state.selectedLabels.filter(
        (label) => label !== action.payload
      );
    },
    clearSelectedLabels: (state) => {
      state.selectedLabels = [];
    },
    setSystemUserToMessenger: (state, action) => {
      state.system_user = action.payload;
    },
    setConversationStatus: (
      state,
      action: { payload: { phone_number: number | string; status: string } }
    ) => {
      const { phone_number, status } = action.payload;
      state.recipients.items = state.recipients.items.map((item) => {
        if (String(item.conversation.phone_number) === String(phone_number)) {
          return {
            ...item,
            conversation: { ...item.conversation, status },
          };
        }
        return item;
      });
    },
    setBrowserNotification: (state, action) => {
      state.isBrowserNotificationOn = action.payload;
      // Persist to localStorage
      localStorage.setItem(
        "browserNotificationEnabled",
        action.payload.toString()
      );
    },
    clearScheduledMessages: (state) => {
      state.scheduledMessages = [];
      state.scheduledMessagesLoading = false;
      state.scheduledMessagesError = null;
    },
    clearLegacyScheduledMessages: (state) => {
      state.legacyScheduledMessages = [];
      state.legacyScheduledMessagesLoading = false;
      state.legacyScheduledMessagesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getGetRecipients
      .addCase(getRecipients.pending, (state, action) => {
        state.loading = true;
        const { page } = action.meta.arg;

        // Only reset the list when fetching page 1 (fresh load: new search, new labels, or initial load).
        // When loading page 2+ we must not clear, so pagination can append correctly.
        if (page === 1) {
          state.recipients = { ...state.recipients, items: [] };
          state.recipientsNextPage = 1;
        }
      })
      .addCase(getRecipients.fulfilled, (state, action) => {
        state.loading = false;
        const { page, searchKeyword, selectedLabels, status } = action.meta.arg;

        // Use requested page to decide replace vs append (avoids race conditions and stale state)
        const isFirstPage = page === 1;

        if (searchKeyword || selectedLabels?.length || status) {
          // Filter mode (labels/search/status): replace on page 1, append on page 2+
          if (isFirstPage) {
            state.recipients = action.payload;
          } else {
            const existingIds = new Set(state.recipients.items.map((item: any) => item.recipient.id));
            const newItems = action.payload.items.filter((item: any) => !existingIds.has(item.recipient.id));
            state.recipients = {
              ...action.payload,
              items: [...state.recipients.items, ...newItems],
            };
          }
        } else {
          // Normal mode: replace on page 1, append on page 2+
          if (isFirstPage) {
            state.recipients = action.payload;
          } else {
            const existingIds = new Set(state.recipients.items.map((item: any) => item.recipient.id));
            const newItems = action.payload.items.filter((item: any) => !existingIds.has(item.recipient.id));
            state.recipients = {
              ...action.payload,
              items: [...state.recipients.items, ...newItems],
            };
          }
        }

        state.recipientsNextPage = action.payload.nextPage;
        state.searchKeyword = searchKeyword || "";
        state.selectedLabels = selectedLabels || [];
      })
      .addCase(getRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle getConversation
      .addCase(getConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(getConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch messages";
      })
      //handle update recipient data
      .addCase(updateRecipient.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRecipient.fulfilled, (state, action) => {
        state.loading = false;
        const recipientIndex = state.recipients.items?.findIndex(
          (item) => item.recipient.id === action.payload.id
        );

        if (recipientIndex !== undefined && recipientIndex !== -1) {
          // Update the specific recipient with the new data
          state.recipients.items[recipientIndex].recipient = {
            ...state.recipients.items[recipientIndex].recipient,
            ...action.payload,
          };

          if (action.payload.id === state.currentRecipient?.id) {
            state.currentRecipient = action.payload;
          }
        }
      })
      .addCase(updateRecipient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error updating recipient note";
      })
      //handle text message sending
      .addCase(sendTextMessage.pending, (state, action) => {
        state.error = null;
        const payload = action.meta.arg as SendTextMessagePayload;
        if (payload.is_schedule) return;
        const recipient = payload.currentRecipient;
        if (
          !recipient?.phone_number ||
          state.currentRecipient?.phone_number !== recipient.phone_number
        ) {
          return;
        }
        if (!state.messages.items) {
          state.messages.items = [];
        }
        const requestId = action.meta.requestId;
        const now = Date.now();
        const phoneNum =
          typeof recipient.phone_number === "number"
            ? recipient.phone_number
            : Number(recipient.phone_number);
        const senderId = payload.senderUserId || state.system_user;
        const sendBy =
          payload.senderDisplayName && payload.senderDisplayName.trim() !== ""
            ? payload.senderDisplayName.trim()
            : undefined;
        const textBody = trimOutgoingCopy(payload.text);
        if (!textBody) return;
        state.messages.items.push({
          id: -(now * 1000 + (Math.floor(Math.random() * 1000) % 1000)),
          created_at: now,
          user_id: senderId,
          phone_number: phoneNum,
          system_user: textBody,
          recipient: "",
          message_id: `optimistic-${requestId}`,
          channel: "whatsapp",
          delivered_time: null,
          read_time: null,
          status: "sent",
          // So SystemUser treats this as plain text → bg-green-100 (same as server text messages)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- minimal shape; UI only reads .type
          system_user_payload: { type: "text" } as any,
          ...(sendBy ? { send_by: sendBy } : {}),
        });
      })
      .addCase(sendTextMessage.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(sendTextMessage.rejected, (state, action) => {
        state.error = action.payload || "Failed to send message";
        const requestId = action.meta.requestId;
        if (state.messages?.items?.length) {
          state.messages.items = state.messages.items.filter(
            (m) => m.message_id !== `optimistic-${requestId}`
          );
        }
      })
      .addCase(handleMediaSend.pending, (state, action) => {
        const payload = action.meta.arg as {
          media_link: string;
          type: string;
          caption: string;
          currentRecipient: Recipient;
          is_schedule?: boolean;
          senderUserId?: string | null;
          senderDisplayName?: string | null;
        };
        if (payload.is_schedule) return;
        const recipient = payload.currentRecipient;
        if (
          !recipient?.phone_number ||
          state.currentRecipient?.phone_number !== recipient.phone_number
        ) {
          return;
        }
        if (!state.messages.items) {
          state.messages.items = [];
        }
        const requestId = action.meta.requestId;
        const now = Date.now();
        const phoneNum =
          typeof recipient.phone_number === "number"
            ? recipient.phone_number
            : Number(recipient.phone_number);
        const senderId = payload.senderUserId || state.system_user;
        const sendBy =
          payload.senderDisplayName && payload.senderDisplayName.trim() !== ""
            ? payload.senderDisplayName.trim()
            : undefined;
        const caption = trimOutgoingCopy(payload.caption);
        const mediaFields = buildOptimisticMediaMessageFields(
          payload.type,
          payload.media_link,
          caption
        );
        state.messages.items.push({
          id: -(now * 1000 + (Math.floor(Math.random() * 1000) % 1000)),
          created_at: now,
          user_id: senderId,
          phone_number: phoneNum,
          recipient: "",
          message_id: `optimistic-${requestId}`,
          channel: "whatsapp",
          delivered_time: null,
          read_time: null,
          status: "sent",
          media_url: mediaFields.media_url,
          system_user: mediaFields.system_user,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- UI-only payload shape
          system_user_payload: mediaFields.system_user_payload as any,
          ...(sendBy ? { send_by: sendBy } : {}),
        });
      })
      .addCase(handleMediaSend.rejected, (state, action) => {
        const requestId = action.meta.requestId;
        if (state.messages?.items?.length) {
          state.messages.items = state.messages.items.filter(
            (m) => m.message_id !== `optimistic-${requestId}`
          );
        }
      })
      //handle update recipient message status
      .addCase(updateMessageStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMessageStatus.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { arg } = action.meta;
        const phone =
          payload.phone_number != null ? payload.phone_number : arg.phone_number;
        const statusVal =
          payload.status != null ? payload.status : arg.status;
        const { message_id } = payload;

        if (message_id && state.messages?.items) {
          const messageIndex = state.messages.items.findIndex(
            (msg) => msg.message_id === message_id
          );
          if (messageIndex !== -1) {
            state.messages.items[messageIndex].status = statusVal;
          }
        }

        if (phone != null && String(phone) !== "") {
          state.recipients.items = state.recipients.items.map((item) =>
            String(item.conversation.phone_number) === String(phone)
              ? {
                  ...item,
                  conversation: { ...item.conversation, status: statusVal },
                }
              : item
          );
        }
        state.loading = false;
      })
      .addCase(updateMessageStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      //handle get bots
      .addCase(getBots.pending, (state) => {
        state.botsLoading = true;
        state.botsError = null;
      })
      .addCase(getBots.fulfilled, (state, action) => {
        state.botsLoading = false;
        state.bots = action.payload;
      })
      .addCase(getBots.rejected, (state, action) => {
        state.botsLoading = false;
        state.botsError = action.payload || "Failed to fetch bots";
      })
      // handle scheduled messages
      .addCase(getScheduledMessages.pending, (state) => {
        state.scheduledMessagesLoading = true;
        state.scheduledMessagesError = null;
      })
      .addCase(getScheduledMessages.fulfilled, (state, action) => {
        state.scheduledMessagesLoading = false;
        state.scheduledMessages = action.payload || [];
      })
      .addCase(getScheduledMessages.rejected, (state, action) => {
        state.scheduledMessagesLoading = false;
        state.scheduledMessagesError = action.payload || "Failed to fetch scheduled messages";
      })
      .addCase(deleteScheduledMessage.pending, (state) => {
        state.scheduledMessagesError = null;
      })
      .addCase(deleteScheduledMessage.fulfilled, (state, action) => {
        const deletedId = action.payload?.id;
        state.scheduledMessages = state.scheduledMessages.filter(
          (item: any) => Number(item?.id) !== Number(deletedId)
        );
      })
      .addCase(deleteScheduledMessage.rejected, (state, action) => {
        state.scheduledMessagesError = action.payload || "Failed to delete scheduled message";
      })
      // handle legacy scheduled messages
      .addCase(getLegacyScheduledMessages.pending, (state) => {
        state.legacyScheduledMessagesLoading = true;
        state.legacyScheduledMessagesError = null;
      })
      .addCase(getLegacyScheduledMessages.fulfilled, (state, action) => {
        state.legacyScheduledMessagesLoading = false;
        state.legacyScheduledMessages = action.payload || [];
      })
      .addCase(getLegacyScheduledMessages.rejected, (state, action) => {
        state.legacyScheduledMessagesLoading = false;
        state.legacyScheduledMessagesError = action.payload || "Failed to fetch legacy scheduled messages";
      })
      .addCase(deleteLegacyScheduledMessage.fulfilled, (state, action) => {
        const deletedId = action.payload?.id;
        state.legacyScheduledMessages = state.legacyScheduledMessages.filter(
          (item: any) => Number(item?.id) !== Number(deletedId)
        );
      })
      .addCase(deleteLegacyScheduledMessage.rejected, (state, action) => {
        state.legacyScheduledMessagesError = action.payload || "Failed to delete legacy scheduled message";
      });
  },
});

export const {
  newRecipientWithMessage,
  newMessageByWebhook,
  setCurrentRecipient,
  setSearchKeyword,
  resetRecipients,
  addSelectedLabel,
  removeSelectedLabel,
  clearSelectedLabels,
  setSystemUserToMessenger,
  setBrowserNotification,
  setConversationStatus,
  clearScheduledMessages,
  clearLegacyScheduledMessages,
} = messengerSlice.actions;
export const messengerReducer = messengerSlice.reducer;
export type { Bot };
